import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Heart, MessageSquare, Send, X, CheckCircle2 } from 'lucide-react';
import useAuthStore from '../store/authStore';

/**
 * Reader-Question-Banner für den Blog.
 *
 * Lädt die aktuell aktive Frage (GET /blog-questions/active). Zeigt einen
 * eleganten Banner im Marken-Look. Leser:innen können ohne Login antworten
 * (anonym oder mit Name + E-Mail). Eingegangene Antworten landen automatisch
 * im Admin-Postfach.
 *
 * Wenn keine Frage aktiv ist → rendert nichts.
 */
const BlogReaderQuestionBanner = () => {
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [answer, setAnswer] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const user = useAuthStore((s) => s.user);

  // localStorage-Key: wir merken uns, welche Fragen der/die Besucher:in schon
  // beantwortet hat – damit der Banner nach dem Absenden nicht erneut auftaucht.
  const storageKey = 'hys_blog_q_answered';
  const getAnswered = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/blog-questions/active');
      const q = r.data;
      if (q && !getAnswered().includes(q.id)) {
        setQuestion(q);
      } else {
        setQuestion(null);
      }
    } catch {
      setQuestion(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!answer.trim()) {
      toast.error('Bitte gib deine Antwort ein');
      return;
    }
    setSending(true);
    try {
      await axiosInstance.post(`/blog-questions/${question.id}/answer`, {
        answer: answer.trim(),
        name: user ? null : (name.trim() || null),
        email: user ? null : (email.trim() || null),
      });
      // Merken, dass beantwortet wurde
      try {
        const list = getAnswered();
        if (!list.includes(question.id)) {
          list.push(question.id);
          localStorage.setItem(storageKey, JSON.stringify(list));
        }
      } catch { /* ignore */ }
      setSubmitted(true);
      toast.success('Vielen Dank für deine Antwort!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Antwort konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  if (loading || !question) return null;

  if (submitted) {
    return (
      <div
        className="relative my-6 p-5 sm:p-6 border-2 border-[#C4A084] bg-gradient-to-br from-[#FAF5EE] to-[#F5EDE4]"
        data-testid="blog-question-banner-success"
      >
        <div className="flex items-center gap-3 text-[#5D4E3C]">
          <div className="p-2 bg-[#C4A084] text-white rounded-full">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold">Vielen Dank für deine Stimme! ♥</p>
            <p className="text-sm text-[#8B7355]">Deine Antwort ist bei uns angekommen.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative my-6 border-2 border-[#C4A084] bg-gradient-to-br from-[#FAF5EE] via-[#F5EDE4] to-[#EFE5D7] shadow-sm"
      data-testid="blog-question-banner"
    >
      {/* Decorative diamond accent */}
      <div className="absolute -top-3 left-6 px-2 py-0.5 bg-[#5D4E3C] text-white text-[10px] tracking-[3px] uppercase">
        ◆ Frage an dich
      </div>

      <div className="p-5 sm:p-6 pt-7">
        <div className="flex items-start gap-3">
          <div className="hidden sm:block p-2 bg-white border border-[#C4A084] shrink-0">
            <MessageSquare className="h-5 w-5 text-[#5D4E3C]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="text-lg sm:text-xl text-[#3D2F22] font-bold leading-snug"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {question.question}
            </h3>
            {question.topic_hint && (
              <p className="text-sm text-[#8B7355] italic mt-1">{question.topic_hint}</p>
            )}

            {!showForm ? (
              <Button
                onClick={() => setShowForm(true)}
                className="mt-3 rounded-none bg-[#5D4E3C] hover:bg-[#3D2F22] text-white text-sm"
                data-testid="blog-question-open-form"
              >
                <Heart className="h-3.5 w-3.5 mr-1.5" /> Sag uns deine Meinung
              </Button>
            ) : (
              <form onSubmit={handleSubmit} className="mt-3 space-y-2" data-testid="blog-question-form">
                <Textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Deine Antwort…"
                  className="rounded-none border-[#C4A084] bg-white resize-none text-sm"
                  rows={3}
                  maxLength={2000}
                  autoFocus
                  data-testid="blog-question-answer-input"
                />
                {!user && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name (optional)"
                      className="rounded-none border-[#C4A084] bg-white text-sm"
                      maxLength={80}
                      data-testid="blog-question-name-input"
                    />
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="E-Mail (optional)"
                      type="email"
                      className="rounded-none border-[#C4A084] bg-white text-sm"
                      maxLength={120}
                      data-testid="blog-question-email-input"
                    />
                  </div>
                )}
                <p className="text-[10px] text-[#A89878]">
                  {user
                    ? `Du antwortest als ${user.first_name || user.email}.`
                    : 'Name & E-Mail sind freiwillig – du kannst auch anonym antworten.'}
                </p>
                <div className="flex gap-2 pt-1">
                  <Button
                    type="submit"
                    disabled={sending || !answer.trim()}
                    className="rounded-none bg-[#5D4E3C] hover:bg-[#3D2F22] text-white text-sm"
                    data-testid="blog-question-submit"
                  >
                    <Send className="h-3.5 w-3.5 mr-1.5" /> {sending ? 'Sende…' : 'Antwort senden'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => { setShowForm(false); setAnswer(''); }}
                    variant="outline"
                    className="rounded-none border-[#C4A084] text-[#5D4E3C] text-sm"
                    data-testid="blog-question-cancel"
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" /> Später
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogReaderQuestionBanner;
